<template>
  <b-container fluid class="">
    <h1 class="m-3">
      {{ title }}
    </h1>
    <commonActions :data-ready="dataReadyTables" :filename-prefix="title"
      @commonFiltersUpdated="commonFiltersUpdated($event)" @clickDownloadPdf="downloadPdf($event)"
      @clickDownloadExcel="downloadExcel($event)" @clickDownloadCsv="downloadCsv($event)" />
    <table-renderer v-for="data of tablesData" :key="data.tableNumber" :table-number="data.tableNumber"
      ref="tableRenderers" :data-table="data.dataTable" :dimension-fields="data.dimensionColumns"
      :measure-fields="data.measureColumns" :table-header="data.tableHeader" :group-headers="groupHeaders"
      :totals-data="data.totalsData" :data-ready="dataReadyTables"
      :hide-groups="hideGroupsByTableNumber[data.tableNumber] === true" @sort-changed="sortChanged" />
  </b-container>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import TableRenderer from "@/components/TableRenderer.vue";
import commonActions from "@/components/pages/commonActions";
import mesureEnum from "@/model/mesureEnum";
import dimensionEnum from "@/model/dimensionEnum";
import {
  columnTypeEnum,
  calculatedColumnsEnum,
  alternativeColumnLabelEnum,
} from "@/model/columnsEnums";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: "ConsoHistory",
  mixins: [abortMixin],
  components: {
    TableRenderer,
    commonActions,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    typeFilter: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      /** Tableau de filtres tels qu'attendu par cubeJs.
       * Ex :
       *    [
       *      {
       *        dimension: "Site.label",
       *        operator: "equals",
       *        values: ["Site 1"]
       *      },
       *      {
       *        dimension: "BusinessDate.id",
       *        operator: "inDateRange",
       *        values: ["2020-09-23", "2020-09-26"]
       *      },
       */
      commonFilters: [],
      siteValues: [],
      timeDimension: null,
      dataReadyTables: false,
      dimensionColumnsTables: [],
      measureColumnsTables: [],
      groupHeaders: [],
      tablesData: [],
      hideGroupsByTableNumber: {},
    };
  },
  mounted() {
    this.initDisplayTables();
  },
  methods: {
    commonFiltersUpdated(value) {
      this.commonFilters = value.filters;
      this.siteValues = value.siteValues;
      this.timeDimension = value.timeDimension || null;
      this.updateTables();
    },
    updateTables() {
      const filters = this.commonFilters.concat([this.typeFilter]);
      var dimensions = [
        dimensionEnum.SNACKPRODUCT_ID,
        dimensionEnum.SNACKPRODUCT_CATEGORY,
        dimensionEnum.SNACKPRODUCT_SUBCATEGORY,
        dimensionEnum.SNACKPRODUCT_PRODUCTNAME,
        dimensionEnum.SNACKHISTORY_TOKENAMOUNTPERITEM,
        dimensionEnum.SITE,
        dimensionEnum.SITE_NAME,
        dimensionEnum.SITE_CURRENCY,
      ];
      this.updateTableList(filters, dimensions);
    },
    async updateTableList(filters, dimensions) {
      try {
        const { signal } = this.resetAbortController();
        var self = this;
        self.dataReadyTables = false;

        self.tablesData = [];
        // Initialisation des objets contenant toutes les données pour chaque site :
        // permet d'afficher dès maintenant les entêtes de tableaux et le loader pour indiquer que les requêtes sont en cours
        self.siteValues.forEach((site, i) => {
          var table = {
            tableNumber: i.toString(),
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
            totalsData: {},
          };
          self.tablesData.push(table);
        });

        var queries = [];

        var orderBy = {};
        orderBy[dimensionEnum.SNACKPRODUCT_CATEGORY] = "asc";
        orderBy[dimensionEnum.SNACKPRODUCT_SUBCATEGORY] = "asc";
        orderBy[dimensionEnum.SNACKPRODUCT_PRODUCTNAME] = "asc";

        queries.push({
          query: {
            measures: [
              mesureEnum.SNACKHISTORY_TOKEN_PRICE,
              mesureEnum.SNACKHISTORY_TOKEN_PRICE_HT,
              mesureEnum.SNACKHISTORY_TOTAL_VAT,
              mesureEnum.SNACKHISTORY_TOTALTOKEN_WITHOUTCB,
              mesureEnum.SNACKHISTORY_EQUIVALENT_EURO_WITHOUTCB,
              mesureEnum.SNACKHISTORY_AMOUNT_CB_TTC,
              mesureEnum.SNACKHISTORY_QUANTITY,
            ],
            dimensions: dimensions,
            filters: filters,
            timeDimensions: this.timeDimension ? [this.timeDimension] : [],
            order: orderBy,
          },
        });

        var resultSets = await TableTools.Helper.doMultipleRequest(queries, { signal });
        var mergedResults = TableTools.Helper.mergeResultsByCommonDimensions(
          resultSets,
          dimensions
        );

        // Extraire les devises par site depuis les résultats
        const siteCurrencies = {};
        mergedResults.forEach(result => {
          const siteId = result[dimensionEnum.SITE];
          if (siteId && result[dimensionEnum.SITE_CURRENCY] && !siteCurrencies[siteId]) {
            siteCurrencies[siteId] = result[dimensionEnum.SITE_CURRENCY];
          }
        });

        // Assigner les devises aux tables
        self.tablesData.forEach(table => {
          table.siteCurrency = siteCurrencies[table.tableHeaderId] || 'EUR';
        });

        // Complétion des objets avec les données reçues pour chaque site
        self.siteValues.forEach((site) => {
          var table = self.tablesData.find(
            (tab) => tab.tableHeaderId === site.id
          );
          const tableSiteCurrency = table.siteCurrency || 'EUR';

          table.dataTable = mergedResults.filter(
            (result) => result[dimensionEnum.SITE] === site.id
          );

          // Renommage de clés pour un libellé différent des autres rapports
          table.dataTable = table.dataTable.map((data) => {
            TableTools.Helper.renameKey(
              data,
              mesureEnum.SNACKHISTORY_AMOUNT_CB_TTC,
              alternativeColumnLabelEnum.SNACKHISTORY_AMOUNT_CB_TTC2
            );
            return data;
          });

          table.totalsData = TableTools.Helper.calculateTotalsData(
            table.dataTable,
            [
              mesureEnum.SNACKHISTORY_TOKEN_PRICE,
              mesureEnum.SNACKHISTORY_TOKEN_PRICE_HT,
              mesureEnum.SNACKHISTORY_TOTAL_VAT,
              mesureEnum.SNACKHISTORY_TOTALTOKEN_WITHOUTCB,
              mesureEnum.SNACKHISTORY_EQUIVALENT_EURO_WITHOUTCB,
              alternativeColumnLabelEnum.SNACKHISTORY_AMOUNT_CB_TTC2,
              mesureEnum.SNACKHISTORY_QUANTITY,
            ]
          );

          //Ajout des lignes de groupement
          table.dataTable = TableTools.Helper.addGroupRows(
            table.dataTable,
            [
              dimensionEnum.SNACKPRODUCT_CATEGORY,
              dimensionEnum.SNACKPRODUCT_SUBCATEGORY,
            ],
            [
              mesureEnum.SNACKHISTORY_TOKEN_PRICE,
              mesureEnum.SNACKHISTORY_TOKEN_PRICE_HT,
              mesureEnum.SNACKHISTORY_TOTAL_VAT,
              mesureEnum.SNACKHISTORY_TOTALTOKEN_WITHOUTCB,
              mesureEnum.SNACKHISTORY_EQUIVALENT_EURO_WITHOUTCB,
              alternativeColumnLabelEnum.SNACKHISTORY_AMOUNT_CB_TTC2,
              mesureEnum.SNACKHISTORY_QUANTITY,
            ]
          );

          // Ajouter la devise à chaque ligne
          table.dataTable.forEach(row => {
            row["Site.currency"] = tableSiteCurrency;
          });

          table.dataTable = self.addPercentageColumns(
            table.dataTable,
            table.totalsData[mesureEnum.SNACKHISTORY_TOKEN_PRICE]
          );

          // Créer les colonnes spécifiques pour ce tableau avec sa devise
          table.dimensionColumns = TableTools.Helper.constructColumnTable(
            [
              dimensionEnum.SNACKPRODUCT_CATEGORY,
              dimensionEnum.SNACKPRODUCT_SUBCATEGORY,
              dimensionEnum.SNACKPRODUCT_PRODUCTNAME,
            ],
            null,
            columnTypeEnum.DIMENSION,
            false,
            null
          );
          table.measureColumns = TableTools.Helper.constructColumnTable(
            [
              dimensionEnum.SNACKHISTORY_TOKENAMOUNTPERITEM,
              mesureEnum.SNACKHISTORY_TOKEN_PRICE,
              calculatedColumnsEnum.PERCENT_REVENUE_PER_SNACK,
              mesureEnum.SNACKHISTORY_TOKEN_PRICE_HT,
              mesureEnum.SNACKHISTORY_TOTAL_VAT,
              alternativeColumnLabelEnum.SNACKHISTORY_AMOUNT_CB_TTC2,
              mesureEnum.SNACKHISTORY_TOTALTOKEN_WITHOUTCB,
              mesureEnum.SNACKHISTORY_EQUIVALENT_EURO_WITHOUTCB,
              mesureEnum.SNACKHISTORY_QUANTITY,
            ],
            null,
            columnTypeEnum.MEASURE,
            true,
            null,
            false,
            tableSiteCurrency
          );
        });

        self.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    /** Initialise ce qui concerne l'affichage commun à tous les tableaux de ce rapport : quelles colonnes doivent réellement être affichées, dans quel ordre
     */
    initDisplayTables() {
      const self = this;
      self.dimensionColumnsTables = TableTools.Helper.constructColumnTable(
        [
          dimensionEnum.SNACKPRODUCT_CATEGORY,
          dimensionEnum.SNACKPRODUCT_SUBCATEGORY,
          dimensionEnum.SNACKPRODUCT_PRODUCTNAME,
        ],
        null,
        columnTypeEnum.DIMENSION,
        false,
        null
      );
      self.measureColumnsTables = TableTools.Helper.constructColumnTable(
        [
          dimensionEnum.SNACKHISTORY_TOKENAMOUNTPERITEM,
          mesureEnum.SNACKHISTORY_TOKEN_PRICE,
          calculatedColumnsEnum.PERCENT_REVENUE_PER_SNACK,
          mesureEnum.SNACKHISTORY_TOKEN_PRICE_HT,
          mesureEnum.SNACKHISTORY_TOTAL_VAT,
          alternativeColumnLabelEnum.SNACKHISTORY_AMOUNT_CB_TTC2,
          mesureEnum.SNACKHISTORY_TOTALTOKEN_WITHOUTCB,
          mesureEnum.SNACKHISTORY_EQUIVALENT_EURO_WITHOUTCB,
          mesureEnum.SNACKHISTORY_QUANTITY,
        ],
        null,
        columnTypeEnum.MEASURE,
        true,
        null
      );

      self.groupHeaders = [
        { nbOfMeasures: 1, label: self.$t("group.unitPrice") },
        {
          nbOfMeasures: 4,
          label: self.$t("group.turnoverEquivalentWithDirectCb"),
        },
        { nbOfMeasures: 4, label: self.$t("group.total") },
      ];
    },
    addPercentageColumns(dataTable, totalRevenue) {
      return dataTable.map((data) => {
        const revenue = data[mesureEnum.SNACKHISTORY_TOKEN_PRICE] || 0;
        data[calculatedColumnsEnum.PERCENT_REVENUE_PER_SNACK] = totalRevenue
          ? (revenue * 100) / totalRevenue
          : 0;

        return data;
      });
    },
    sortChanged(e) {
      this.$set(
        this.hideGroupsByTableNumber,
        e.tableNumber,
        e.event.sortBy !== ""
      );
    },
    downloadPdf(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      const self = this;
      var datasForExport = [];

      self.tablesData.forEach((table, index) => {
        const renderer = self.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || self.defaultSortedColumnTables;

        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          table.dimensionColumns,
          table.measureColumns,
          self.groupHeaders,
          table.tableHeader,
          sortKey,
          null,
          table.totalsData
        );
        datasForExport.push(data);
      });

      TableTools.Helper.storeExportData(
        datasForExport,
        self.title,
        subtitles,
        filename
      );
    },
  },
};
</script>
